import { ajax } from 'cadenza/api-client/api';
import type { DataProtectionAssignmentDto } from 'cadenza/api-client/dto/data-protection-assignment-dto';
import type { DataProtectionContextConfirmDeletionDto } from 'cadenza/api-client/dto/data-protection-context-confirm-deletion-dto';
import type {
  DataProtectionContextDto,
  DataProtectionContextListingDto,
  MutableDataProtectionContextDto,
} from 'cadenza/api-client/dto/data-protection-context-dto';
import type { DataProtectionContextLogDto } from 'cadenza/api-client/dto/data-protection-context-log-dto';
import type { DataProtectionContextProlongDeletionDto } from 'cadenza/api-client/dto/data-protection-context-prolong-deletion-dto';
import type { SimpleDataProtectionContextDto } from 'cadenza/api-client/dto/simple-data-protection-context-dto';
import { type TableData, type TableParams, tableParams } from 'cadenza/api-client/tables-api';
import { isFeatureAvailable } from 'cadenza/features';
import { MIMETYPE_JSON } from 'cadenza/model/media-type';
import type { DataProtectionContextId } from 'cadenza/repository/model/data-protection-context-id.gen';
import type { ObjecttypeIdString } from 'cadenza/repository/model/objecttype-id.gen';
import type { RepositoryName } from 'cadenza/repository/model/repository-name.gen';
import type { WorkbookId } from 'cadenza/repository/model/workbook-id.gen';

export function getDataProtectionContexts(params: TableParams, getDeleted = false) {
  const urlSearchParams = tableParams(params);
  urlSearchParams.set('getDeleted', String(getDeleted));
  return ajax<TableData<DataProtectionContextListingDto>>(`/data-protection-contexts?${urlSearchParams}`, {
    dataType: 'json',
  });
}

export function createDataProtectionContext(
  repositoryName: RepositoryName,
  dataProtectionContext: Omit<MutableDataProtectionContextDto, 'deletionDateConfirmed'>,
) {
  return ajax<DataProtectionContextId>(`/repositories/${repositoryName}/data-protection-contexts`, {
    method: 'POST',
    contentType: MIMETYPE_JSON,
    data: JSON.stringify(dataProtectionContext),
  });
}

export function getDataProtectionContextDto({ repositoryName, itemName }: DataProtectionContextId) {
  return ajax<DataProtectionContextDto>(`/repositories/${repositoryName}/data-protection-contexts/${itemName}`, {
    dataType: 'json',
  });
}

export function getSimpleDataProtectionContextsForRepository(repositoryName: RepositoryName) {
  if (isFeatureAvailable('DATA_PROTECTION')) {
    return ajax<SimpleDataProtectionContextDto[]>(`/repositories/${repositoryName}/data-protection-contexts`, {
      dataType: 'json',
    });
  } else {
    return Promise.resolve([]);
  }
}

export function getDataProtectionContextLogDtos({ repositoryName, itemName }: DataProtectionContextId) {
  return ajax<DataProtectionContextLogDto[]>(
    `/repositories/${repositoryName}/data-protection-context/${itemName}/logs`,
    {
      dataType: 'json',
    },
  );
}

export function getDataProtectionContextAssignmentDtos({ repositoryName, itemName }: DataProtectionContextId) {
  return ajax<DataProtectionAssignmentDto[]>(
    `/repositories/${repositoryName}/data-protection-contexts/${itemName}/assignments`,
    {
      dataType: 'json',
    },
  );
}

export function patchDataProtectionContext(
  { repositoryName, itemName }: DataProtectionContextId,
  patch: Partial<MutableDataProtectionContextDto>,
) {
  return ajax<DataProtectionContextDto>(`/repositories/${repositoryName}/data-protection-contexts/${itemName}`, {
    type: 'PATCH',
    contentType: MIMETYPE_JSON,
    data: JSON.stringify(patch),
    dataType: 'json',
  });
}

export function deleteDataProtectionContext({ repositoryName, itemName }: DataProtectionContextId) {
  return ajax<void>(`/repositories/${repositoryName}/data-protection-contexts/${itemName}`, {
    type: 'DELETE',
  });
}

export function prolongDataProtectionContextDeletionDate(
  { repositoryName, itemName }: DataProtectionContextId,
  prolongDeletionDto: DataProtectionContextProlongDeletionDto,
) {
  return ajax<void>(`/repositories/${repositoryName}/data-protection-contexts/${itemName}/prolong-deletion`, {
    contentType: MIMETYPE_JSON,
    data: JSON.stringify(prolongDeletionDto),
    type: 'POST',
  });
}

export function confirmDataProtectionContextDeletionDate(
  { repositoryName, itemName }: DataProtectionContextId,
  deletionMoratoriumDto: DataProtectionContextConfirmDeletionDto,
) {
  return ajax<void>(`/repositories/${repositoryName}/data-protection-contexts/${itemName}/confirm-deletion`, {
    contentType: MIMETYPE_JSON,
    data: JSON.stringify(deletionMoratoriumDto),
    type: 'POST',
  });
}

export function getObjecttypeDataProtectionContext(objecttypeId: ObjecttypeIdString) {
  return ajax<SimpleDataProtectionContextDto>(`/objecttypes/${window.btoa(objecttypeId)}/data-protection-context`);
}

export function getWorkbookDataProtectionContext({ repositoryName, itemName }: WorkbookId) {
  return ajax<SimpleDataProtectionContextDto>(
    `/repositories/${repositoryName}/workbooks/${itemName}/data-protection-context`,
  );
}

export function updateObjecttypeDataProtectionContext(
  objecttypeId: ObjecttypeIdString,
  dataProtectionContextDto: SimpleDataProtectionContextDto,
) {
  return ajax<SimpleDataProtectionContextDto>(`/objecttypes/${window.btoa(objecttypeId)}/data-protection-context`, {
    method: 'PUT',
    contentType: MIMETYPE_JSON,
    data: JSON.stringify(dataProtectionContextDto),
  });
}
